import { Injectable, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { CategoryDetail, Product } from 'src/app/products/models/product.model';
import { TitleService } from '../page-title/title.service';
import { StoreConfigurationRepositoryService } from '../store-configuration/store-configuration-repository.service';
import { LogoType } from '../../interfaces/store-configuration.interface';

export class SEO {
  constructor(
    public keywords?: string[],
    public also_known_as_names?: string[],
    public meta_title?: string,
    public meta_description?: string
  ) { }
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private titleService = inject(TitleService);
  private meta = inject(Meta);
  private store = inject(StoreConfigurationRepositoryService);


  // url thumbnail
  updateThumbnailMetaTags() {
    const storeConfig = this.store.storeConfigurations();
    const rectangularLogo = storeConfig?.logos?.filter(
      (logo: any) => logo.format === LogoType.Rectangular
    ) || [];

    const sales_channel_code = storeConfig?.sales_channel_code;
    const webstoreLogo = sales_channel_code === "d9c7dc" ? 'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/imaginext_logo_share.png' : (rectangularLogo.length && rectangularLogo[0]?.url) ? rectangularLogo[0].url : '';

    // og:image for Imaginext is made static and for other webstore it is retrived from rectangularLogo
    this.meta.updateTag({
      property: 'og:image',
      content: webstoreLogo
    });

  }

  updateTitle(title: string) {
    this.titleService.setTitleWithCompanyName(title);
  }

  updateDescription(desc: string) {
    this.meta.updateTag({ name: 'description', content: desc });
  }

  updateMeta(name = '', content = '') {
    if (name && content) {
      this.meta.updateTag({ name, content });
    }
  }

  updateProductName(name: string) {
    if (!name) {
      return;
    }
    this.meta.updateTag({ name: 'product', content: name });
  }

  updateSeoInformationApp(seo: SEO) {
    const combined = [...seo?.keywords ?? [], ...seo?.also_known_as_names ?? []].join(',');

    if (combined && combined !== '') {
      this.meta.updateTag({ name: 'keywords', content: combined });
    }

    if (seo?.meta_title) {
      this.meta.addTag({ name: 'title', content: seo?.meta_title },);
    }
    if (seo?.meta_description) {
      this.meta.addTag({ name: 'description', content: seo?.meta_description });
    }
  }

  updateSeoInformation(product: Product) {
    const { seo_detail = {}, featured_images = [] } = product || {};

    if (seo_detail) {
      const { keywords = [], also_known_as_names = [], meta_description = '', meta_title = '' } = seo_detail;

      const combined = [...keywords, ...also_known_as_names].join(',');

      if (combined && combined !== '') {
        this.meta.updateTag({ name: 'keywords', content: combined });
      }

      if (meta_title) {
        this.meta.updateTag({ name: 'title', content: meta_title });
      }

      if (meta_description) {
        this.meta.updateTag({ name: 'description', content: meta_description });
      }
    }

    if (Array.isArray(featured_images) && featured_images.length) {
      const imageUrl = featured_images[0]?.url;
      if (imageUrl) {
        this.meta.updateTag({
          property: 'og:image',
          content: imageUrl
        });
      }
    }
  }

  updateSeoInformationForCategory(category: CategoryDetail) {

    const { name, meta_title, meta_description, search_keywords } = category || {};

    if (name) {
      this.meta.updateTag({ name: 'category', content: name });
    }

    if (meta_title) {
      this.meta.updateTag({ name: 'title', content: meta_title });
    }

    if (meta_description) {
      this.meta.updateTag({ name: 'description', content: meta_description });
    }

    if (Array.isArray(search_keywords) && search_keywords.length) {
      const metaKeywords = search_keywords.join(",");
      this.meta.updateTag({ name: 'keywords', content: metaKeywords });
    }
  }

  addRobotsMeta() {
    this.meta.addTag({ name: 'robots', content: 'index, follow' });
  }

  updateMetaTags(headScript: string) {
    const metaTags = this.prepareMetaObjectArray(this.getMetaTagLines(headScript));
    metaTags.forEach(meta => {
      const { name, content } = meta;
      this.updateMeta(name, content);
    });
    this.updateThumbnailMetaTags();
  }

  private getMetaTagLines(headScript: string): string {
    if (!headScript || (typeof headScript !== 'string')) {
      return '';
    }
    headScript = headScript.replace(/(\r\n|\n|\r)/gm, '');
    const START = `<!-- Start of Meta Tags -->`;
    const END = `<!-- End of Meta Tags -->`;
    /* const regex = `(?<=${START})(.*)(?=${END})`;
    const match = new RegExp(regex).exec(headScript.trim())?.[0];
    return match ? match : ''; */
    return this.extractString(headScript, START, END);
  }

  private extractString(input: string, prefix: string, suffix: string): string {

    let i = input.indexOf(prefix);
    if (i >= 0) {
      input = input.substring(i + prefix.length);
    } else {
      return '';
    }

    if (suffix) {
      i = input.indexOf(suffix);
      if (i >= 0) {
        input = input.substring(0, i);
      } else {
        return '';
      }
    }
    return input;
  }

  private prepareMetaObjectArray(headScript: string): { name: string, content: string }[] {
    const arr = headScript.split('>').filter(x => x);
    return arr.
      filter(
        meta => meta.includes('name') && meta.includes('content')
      ).
      map(meta => {
        const NAME_KEYWORD = 'name';
        const CONTENT_KEYWORD = 'content';
        const END = '="';
        const startIndexName = meta.lastIndexOf(`${NAME_KEYWORD}${END}`) + NAME_KEYWORD.length + END.length;
        const startIndexContent = meta.lastIndexOf(`${CONTENT_KEYWORD}${END}`) + CONTENT_KEYWORD.length + END.length;
        // end index will be when value ends, index of will skip the first match
        const endIndexName = meta.indexOf('"', startIndexName + 1);
        const endIndexContent = meta.indexOf('"', startIndexContent + 1);
        return ({
          name: meta.substring(startIndexName, endIndexName),
          content: meta.substring(startIndexContent, endIndexContent)
        });
      }).filter(
        meta => meta?.name && meta?.content
      );
  }

}
